import {Button} from '@/shadcn_components/ui/button';
import useWorkflowStore from '../store';
import {useCallback} from 'react';
import {JsonViewButton} from '@/components/json-view-button';
import {useSaveWorkflow} from '../hooks/useSaveWorkflow';
import {useAddNoNodeTasks} from '../hooks/useAddNoNodeTasks';
import {BaseKey, BaseRecord} from '@refinedev/core';
import {Undo, Redo, KanbanIcon, ListXIcon} from 'lucide-react';
import {useImportWorkflow} from '../hooks/useImportWorkflow';
import {useExportWorkflow} from '../hooks/useExportWorkflow';
import {arrange_nodes_v2, createTimelineMarkersFromNodes} from '../utils/arrange_nodes_v2';
import {normalize_workflow_nodes} from '../utils/auto_calculate_dates';
import {Checkbox} from '@/shadcn_components/ui/checkbox';

export const Toolbar = ({
  resource,
  id,
  record,
}: {
  resource: 'projects' | 'tasks';
  id: BaseKey;
  record: BaseRecord;
}) => {
  const store = useWorkflowStore();
  const {
    nodes,
    setNodes,
    edges,
    setEdges,
    viewport,
    changes,
    undo,
    redo,
    canUndo,
    canRedo,
    historyIndex,
    history,
    timelineMarkers,
    setTimelineMarkers,
  } = store;

  // save data
  const {onSubmit, isSaving} = useSaveWorkflow(resource, id);

  const {no_node_tasks, addNoNodeTasks} = useAddNoNodeTasks();

  // auto calculate date
  const autoCalculateDates = useCallback(() => {
    const {
      nodes: newNodes,
      edges: newEdges,
      error,
    } = normalize_workflow_nodes(nodes, edges, record?.expected_work_days);
    setNodes(newNodes);
    setEdges(newEdges);

    if (error) {
      alert(error);
    }
  }, [nodes, edges, setNodes, setEdges]);

  // auto create timeline markers
  const autoCreateTimelineMarkers = useCallback(() => {
    const {
      nodes: newNodes,
      edges: newEdges,
      error,
    } = normalize_workflow_nodes(nodes, edges, record?.expected_work_days);
    setNodes(newNodes);
    setEdges(newEdges);

    if (error) {
      alert(error);
    } else {
      const markers = createTimelineMarkersFromNodes(newNodes);
      setTimelineMarkers(markers);
    }
  }, [nodes, edges, setNodes, setEdges, setTimelineMarkers]);

  // clear timeline markers
  const clearTimelineMarkers = useCallback(() => {
    setTimelineMarkers([]);
  }, [setTimelineMarkers]);

  const importWorkflow = useImportWorkflow();
  const exportWorkflow = useExportWorkflow();

  const arrange_nodes = useCallback(() => {
    const wf = normalize_workflow_nodes(nodes, edges, record?.expected_work_days);

    if (wf.error) {
      alert(wf.error);
      return;
    }

    const {newEdges, newNodes, timelineMarkers} = arrange_nodes_v2(wf.nodes, wf.edges);
    setNodes(newNodes);
    setEdges(newEdges);
    setTimelineMarkers(timelineMarkers);
  }, [nodes, edges, setNodes, setEdges, setTimelineMarkers]);

  const doSave = useCallback(async () => {
    const {
      nodes: calculatedNodes,
      edges: calculatedEdges,
      error,
    } = normalize_workflow_nodes(nodes, edges, record?.expected_work_days);

    if (error) {
      alert(error);
      return;
    }

    setNodes(calculatedNodes);
    setEdges(calculatedEdges);
    setTimeout(() => onSubmit(), 0); // call in next render cycle
  }, [onSubmit, nodes, edges, setNodes, setEdges]);

  return (
    <div className="flex items-center justify-between py-4 px-6 bg-gray-100 border-b border-gray-200">
      <div className="flex items-center space-x-4">
        <Button variant="default" size="sm" type="button" disabled={isSaving} onClick={doSave}>
          Lưu
        </Button>

        <div className="flex space-x-2 items-center">
          <Button
            variant="outline"
            size="icon"
            disabled={!canUndo}
            onClick={undo}
            title="Hoàn tác (Ctrl+Z)">
            <Undo className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            disabled={!canRedo}
            onClick={redo}
            title="Làm lại (Ctrl+Y)">
            <Redo className="h-4 w-4" />
          </Button>
        </div>

        {no_node_tasks.length > 0 && (
          <Button variant="default" size="sm" type="button" onClick={addNoNodeTasks}>
            Công việc chưa gán ({no_node_tasks.length})
          </Button>
        )}

        <Button variant="default" size="sm" type="button" onClick={autoCalculateDates}>
          Tính ngày
        </Button>

        <Button variant="default" size="sm" type="button" onClick={arrange_nodes}>
          Sắp xếp
        </Button>

        <Button
          variant="outline"
          size="sm"
          type="button"
          onClick={autoCreateTimelineMarkers}
          title="Tạo mốc thời gian">
          <KanbanIcon />
        </Button>

        <Button
          variant="outline"
          size="sm"
          type="button"
          onClick={clearTimelineMarkers}
          title="Xóa mốc thời gian">
          <ListXIcon />
        </Button>

        <div className="flex space-x-2 items-center">
          <Button variant="outline" size="sm" onClick={exportWorkflow}>
            Export
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => document.getElementById('import-workflow')?.click()}>
            Import
          </Button>
          <input
            id="import-workflow"
            type="file"
            className="hidden"
            accept=".json"
            onChange={importWorkflow}
          />
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="show-details"
            checked={store.options.show_details}
            onCheckedChange={checked => store.setOptions({show_details: checked === true})}
          />
          <label
            htmlFor="show-details"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            Hiển thị chi tiết
          </label>
        </div>

        {process.env.NODE_ENV === 'development' && (
          <JsonViewButton
            data={{
              historyIndex,
              historyDeep: history.length,
              canUndo,
              canRedo,
              changes,
              viewport,
              no_node_tasks,
              nodes,
              edges,
            }}
            title="Workflow Data"
          />
        )}
      </div>
    </div>
  );
};
